import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

// ------------------------------------------------------------------ botão

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'danger' | 'outline';
  size?: 'md' | 'lg';
  loading?: boolean;
  full?: boolean;
};

const VARIANTS: Record<string, string> = {
  primary: 'bg-brand text-brand-ink shadow-lg shadow-brand/25 hover:brightness-110 active:scale-[0.98]',
  outline: 'border border-line text-ink hover:bg-surface-2 active:scale-[0.98]',
  ghost: 'text-muted hover:bg-surface-2 hover:text-ink',
  danger: 'bg-danger text-white shadow-lg shadow-danger/25 hover:brightness-110 active:scale-[0.98]',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  full,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-2xl font-semibold',
        'transition duration-150 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none',
        // Alvo de toque nunca menor que 44px: o motorista aperta isto com
        // o carro andando.
        size === 'lg' ? 'min-h-14 px-6 text-lg' : 'min-h-11 px-4 text-base',
        full && 'w-full',
        VARIANTS[variant],
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
        className,
      )}
    >
      {loading && <Spinner className="h-4 w-4" />}
      {children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cx('animate-spin', className ?? 'h-5 w-5')}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// ------------------------------------------------------------------ campos

type InputProps = InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean };

export function Input({ className, invalid, ...rest }: InputProps) {
  return (
    <input
      {...rest}
      aria-invalid={invalid || undefined}
      className={cx(
        // text-base (16px) obrigatório: fonte menor faz o iOS dar zoom ao
        // focar o campo e a tela inteira sai do lugar.
        'w-full rounded-2xl bg-surface-2 px-4 py-3.5 text-base text-ink placeholder:text-faint',
        'border transition outline-none',
        invalid ? 'border-danger' : 'border-line focus:border-brand focus:ring-4 focus:ring-brand/15',
        className,
      )}
    />
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium text-muted">{label}</span>
      {children}
      {error ? (
        <span className="block text-sm text-danger">{error}</span>
      ) : hint ? (
        <span className="block text-sm text-faint">{hint}</span>
      ) : null}
    </label>
  );
}

// ---------------------------------------------------------------- estrutura

/**
 * Fundo do cartão é decidido aqui, por `tone`.
 *
 * Passar `bg-*` por className não funciona: a classe da base e a de fora
 * têm a mesma especificidade, e quem vence é a ordem do CSS gerado, não a
 * ordem em que foram escritas. Foi assim que o cartão de saldo saiu com
 * fundo escuro e texto quase preto.
 */
export type CardTone = 'surface' | 'brand' | 'brandSoft' | 'danger' | 'warn' | 'good';

const CARD_TONES: Record<CardTone, string> = {
  surface: 'border-line bg-surface',
  brand:
    'border-transparent bg-[linear-gradient(135deg,var(--c-brand),var(--c-brand-soft))] text-brand-ink shadow-xl shadow-brand/20',
  brandSoft: 'border-brand/40 bg-brand/10',
  danger: 'border-danger/40 bg-danger/10',
  warn: 'border-warn/40 bg-warn/10',
  good: 'border-good/40 bg-good/10',
};

export function Card({
  tone = 'surface',
  className,
  children,
}: {
  tone?: CardTone;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cx('rounded-3xl border p-5', CARD_TONES[tone], className)}>{children}</div>
  );
}

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'good' | 'warn' | 'bad' | 'brand';
}) {
  const tones = {
    neutral: 'bg-surface-2 text-muted',
    good: 'bg-good/15 text-good',
    warn: 'bg-warn/15 text-warn',
    bad: 'bg-danger/15 text-danger',
    brand: 'bg-brand/15 text-brand',
  } as const;
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold',
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

/**
 * Painel inferior. É o padrão de app de mobilidade: o mapa ocupa a tela e
 * a interação mora embaixo, ao alcance do polegar.
 */
export function Sheet({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cx(
        'rounded-t-[2rem] border-t border-line bg-surface',
        'shadow-[0_-8px_40px_rgba(0,0,0,0.35)]',
        'px-5 pt-4 pb-[max(1.25rem,env(safe-area-inset-bottom))]',
        // Em tela larga o painel não estica de ponta a ponta.
        'mx-auto w-full sm:max-w-xl sm:rounded-b-[2rem] sm:border sm:mb-4',
        className,
      )}
    >
      <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-line" aria-hidden="true" />
      {children}
    </div>
  );
}

/** Coluna central das telas de conteúdo. Cresce um pouco no tablet. */
export function Page({
  title,
  subtitle,
  children,
}: {
  title?: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto w-full max-w-md px-5 pb-10 pt-safe sm:max-w-xl sm:px-8">
        {title && (
          <header className="mb-6 mt-4">
            <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
            {subtitle && <p className="mt-1 text-muted">{subtitle}</p>}
          </header>
        )}
        {children}
      </div>
    </div>
  );
}

export function Stars({ value, count }: { value?: number | null; count?: number }) {
  if (value == null) return <span className="text-sm text-faint">sem avaliações</span>;
  return (
    <span className="inline-flex items-center gap-1 text-sm text-muted">
      <svg viewBox="0 0 20 20" className="h-4 w-4 fill-warn" aria-hidden="true">
        <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L10 14.9 4.8 17.6l1-5.8L1.5 7.7l5.9-.9z" />
      </svg>
      {value.toFixed(1)}
      {count !== undefined && <span className="text-faint">({count})</span>}
    </span>
  );
}

export function Empty({
  title,
  hint,
  icon,
}: {
  title: string;
  hint?: string;
  icon?: ReactNode;
}) {
  return (
    <div className="rounded-3xl border border-dashed border-line px-6 py-14 text-center">
      {icon && (
        <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-surface-2 text-faint">
          {icon}
        </div>
      )}
      <p className="font-semibold text-ink">{title}</p>
      {hint && <p className="mx-auto mt-1 max-w-xs text-sm text-faint">{hint}</p>}
    </div>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="rounded-2xl bg-danger/10 px-4 py-3 text-sm text-danger">
      {children}
    </p>
  );
}

/** Linha de lista com ícone à esquerda e valor à direita. */
export function Row({
  icon,
  title,
  subtitle,
  meta,
  value,
  valueTone = 'ink',
}: {
  icon?: ReactNode;
  title: string;
  subtitle?: string;
  meta?: string;
  value?: ReactNode;
  valueTone?: 'ink' | 'good' | 'muted';
}) {
  const tones = { ink: 'text-ink', good: 'text-good', muted: 'text-muted' } as const;
  return (
    <div className="flex items-center gap-4 py-4">
      {icon && (
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-surface-2 text-muted">
          {icon}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{title}</p>
        {subtitle && <p className="truncate text-sm text-muted">{subtitle}</p>}
        {meta && <p className="mt-0.5 text-xs text-faint">{meta}</p>}
      </div>
      {value !== undefined && (
        <span className={cx('tabular shrink-0 font-semibold', tones[valueTone])}>{value}</span>
      )}
    </div>
  );
}
